import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { DataCard } from '@/components/ui/data-card';
import { StatusBadge } from '@/components/ui/status-badge';
import { 
  Activity, ShieldCheck, AlertTriangle, Users, TrendingUp, Clock, CheckCircle, Bell,
  Calendar, Package, Warehouse, BarChart2, PieChart as PieChartIcon, ShieldOff, FileSignature
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime, formatDate, formatTime } from '@/utils/dateFormat';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [advancedMetrics, setAdvancedMetrics] = useState(null);
  const [bloqueados, setBloqueados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchData();
    
    const interval = setInterval(() => {
      checkExpiringMaterials();
    }, 300000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, advancedRes, bloqueadosRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/metricas-avancadas'),
        api.get('/dashboard/contratos-bloqueados')
      ]);
      setStats(statsRes.data);
      setAdvancedMetrics(advancedRes.data);
      setBloqueados(bloqueadosRes.data);
      
      if (statsRes.data.alertas_validade?.length > 0) {
        const alertasAlta = statsRes.data.alertas_validade.filter(a => a.urgencia === 'alta');
        if (alertasAlta.length > 0) {
          toast.warning(`${alertasAlta.length} material(is) vencendo em menos de 3 dias!`, {
            duration: 5000,
            action: {
              label: 'Ver',
              onClick: () => setShowNotifications(true)
            }
          });
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const checkExpiringMaterials = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      if (response.data.alertas_validade?.length > 0) {
        const alertasAlta = response.data.alertas_validade.filter(a => a.urgencia === 'alta');
        if (alertasAlta.length > 0) {
          toast.error(`⚠️ ${alertasAlta.length} material(is) vencendo em breve!`);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar validades:', error);
    }
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Visão geral do sistema CME</p>
        </div>
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative p-3 rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="btn-notifications"
        >
          <Bell className="w-6 h-6 text-slate-600" />
          {stats?.alertas_validade?.length > 0 && (
            <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {stats.alertas_validade.length}
            </span>
          )}
        </button>
      </div>

      {/* Alertas de Validade */}
      {showNotifications && stats?.alertas_validade?.length > 0 && (
        <Alert className="border-blue-200 bg-blue-50">
          <AlertTriangle className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900">Alertas de Validade</AlertTitle>
          <AlertDescription className="mt-2 space-y-2">
            {stats.alertas_validade.map((alerta, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-blue-200">
                <div>
                  <span className="font-medium">{alerta.ciclo_numero}</span> - {alerta.cliente_nome}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${alerta.urgencia === 'alta' ? 'text-red-600' : 'text-blue-600'}`}>
                    {alerta.dias_restantes} dia(s)
                  </span>
                  <StatusBadge status={alerta.urgencia === 'alta' ? 'vencido' : 'media'} />
                </div>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <DataCard
          title="Ciclos Hoje"
          value={stats?.ciclos_hoje || 0}
          icon={Activity}
          description="Realizados hoje"
        />
        <DataCard
          title="Taxa Aprovação"
          value={`${stats?.taxa_aprovacao || 0}%`}
          icon={CheckCircle}
          description="Últimos 30 dias"
        />
        <DataCard
          title="Tempo Médio"
          value={`${stats?.tempo_medio_ciclo || 0} min`}
          icon={Clock}
          description="Duração do ciclo"
        />
        <DataCard
          title="Alertas"
          value={stats?.ciclos_vencendo || 0}
          icon={AlertTriangle}
          description="Vencendo em 7 dias"
        />
        <DataCard
          title="Agendamentos"
          value={advancedMetrics?.agendamentos_hoje || 0}
          icon={Calendar}
          description="Hoje"
        />
        <DataCard
          title="Arsenal"
          value={advancedMetrics?.arsenal_aguardando || 0}
          icon={Warehouse}
          description="Aguardando retirada"
        />
      </div>

      {/* Painel de Clientes Bloqueados */}
      {bloqueados && bloqueados.total_bloqueados > 0 && (
        <div className="bg-white border-2 border-red-200 rounded-lg p-5 shadow-sm" data-testid="painel-bloqueados">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ShieldOff className="w-5 h-5 text-red-600" />
              Clientes Bloqueados
              <span className="ml-2 px-2.5 py-0.5 bg-red-100 text-red-700 text-sm font-bold rounded-full">
                {bloqueados.total_bloqueados}
              </span>
            </h3>
            <div className="flex gap-3 text-sm">
              {bloqueados.inativos > 0 && (
                <span className="px-2 py-1 bg-red-50 text-red-700 rounded">
                  {bloqueados.inativos} inativo(s)
                </span>
              )}
              {bloqueados.vencidos > 0 && (
                <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded">
                  {bloqueados.vencidos} vencido(s)
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {bloqueados.clientes?.map((cliente, idx) => (
              <div 
                key={idx} 
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  cliente.status === 'inativo' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}
                data-testid={`bloqueado-${idx}`}
              >
                <div className="flex-1">
                  <span className="font-medium text-slate-900">{cliente.cliente_nome}</span>
                  <span className="ml-2 text-sm text-slate-500">{cliente.numero_contrato}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    cliente.status === 'inativo' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {cliente.status === 'inativo' ? 'Inativo' : 'Vencido'}
                  </span>
                  <span className="text-xs text-slate-500">{cliente.motivo}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            <FileSignature className="w-3 h-3 inline mr-1" />
            Recepção de materiais bloqueada para estes clientes. Regularize via Financeiro &gt; Contratos.
          </p>
        </div>
      )}

      {/* Painel de Contratos Vencendo em 60 dias */}
      {bloqueados && bloqueados.vencendo_60_dias > 0 && (
        <div className="bg-white border-2 border-amber-200 rounded-lg p-5 shadow-sm" data-testid="painel-vencendo-60">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Contratos Vencendo em 60 dias
              <span className="ml-2 px-2.5 py-0.5 bg-amber-100 text-amber-700 text-sm font-bold rounded-full">
                {bloqueados.vencendo_60_dias}
              </span>
            </h3>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {bloqueados.contratos_vencendo?.map((contrato, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid={`vencendo-${idx}`}>
                <div className="flex-1">
                  <span className="font-medium text-slate-900">{contrato.cliente_nome}</span>
                  <span className="ml-2 text-sm text-slate-500">{contrato.numero_contrato}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    contrato.dias_restantes <= 15 ? 'bg-red-100 text-red-700' :
                    contrato.dias_restantes <= 30 ? 'bg-amber-100 text-amber-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {contrato.dias_restantes} dias
                  </span>
                  <span className="text-xs text-slate-500">Vence: {contrato.data_fim?.split('T')[0]}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Providencie a renovação para evitar bloqueio automático.
          </p>
        </div>
      )}

      {/* Tabs for different views */}
      <Tabs defaultValue="monitor" className="space-y-4">
        <TabsList>
          <TabsTrigger value="monitor">🔴 Monitoramento</TabsTrigger>
          <TabsTrigger value="gerencial">📊 Gerencial</TabsTrigger>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="trends">Tendências</TabsTrigger>
        </TabsList>

        {/* Monitor Tab - Real-time */}
        <TabsContent value="monitor" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Ciclos em Andamento */}
            <div className="bg-white border-2 border-blue-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600 animate-pulse" />
                Ciclos em Andamento
              </h3>
              <div className="space-y-3">
                {stats?.recent_cycles?.filter(c => c.resultado === 'em_processo' || c.status === 'em_processo').length > 0 ? (
                  stats.recent_cycles
                    .filter(c => c.resultado === 'em_processo' || c.status === 'em_processo')
                    .map((ciclo) => (
                      <div key={ciclo.id} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-blue-900">{ciclo.numero_ciclo}</span>
                          <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full animate-pulse">
                            Em processo
                          </span>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">{ciclo.autoclave_nome || 'Autoclave'}</p>
                        <p className="text-xs text-blue-600 mt-1">
                          Iniciado: {formatTime(ciclo.data_hora)}
                        </p>
                      </div>
                    ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Activity className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Nenhum ciclo em andamento</p>
                  </div>
                )}
              </div>
            </div>

            {/* Arsenal - Aguardando Retirada */}
            <div className="bg-white border-2 border-amber-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Warehouse className="w-5 h-5 text-amber-600" />
                Arsenal - Aguardando
                {advancedMetrics?.arsenal_aguardando > 0 && (
                  <span className="ml-auto px-2 py-1 bg-amber-100 text-amber-800 text-sm rounded-full">
                    {advancedMetrics.arsenal_aguardando}
                  </span>
                )}
              </h3>
              {advancedMetrics?.arsenal_aguardando > 0 ? (
                <div className="space-y-2">
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-amber-700">{advancedMetrics.arsenal_aguardando}</p>
                    <p className="text-sm text-amber-600">material(is) aguardando retirada</p>
                  </div>
                  <a 
                    href="/arsenal" 
                    className="block text-center text-sm text-amber-700 hover:text-amber-800 underline"
                  >
                    Ver detalhes no Arsenal →
                  </a>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                  <p>Todos os materiais foram retirados</p>
                </div>
              )}
            </div>

            {/* Agendamentos de Hoje */}
            <div className="bg-white border-2 border-green-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                Agendamentos Hoje
                {advancedMetrics?.agendamentos_hoje > 0 && (
                  <span className="ml-auto px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                    {advancedMetrics.agendamentos_hoje}
                  </span>
                )}
              </h3>
              {advancedMetrics?.agendamentos_hoje > 0 ? (
                <div className="space-y-2">
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-700">{advancedMetrics.agendamentos_hoje}</p>
                    <p className="text-sm text-green-600">agendamento(s) para hoje</p>
                  </div>
                  <a 
                    href="/agendamentos" 
                    className="block text-center text-sm text-green-700 hover:text-green-800 underline"
                  >
                    Ver agendamentos →
                  </a>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nenhum agendamento para hoje</p>
                </div>
              )}
            </div>
          </div>

          {/* Alertas de Validade */}
          <div className="bg-white border-2 border-red-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Alertas de Validade
              {stats?.alertas_validade?.length > 0 && (
                <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                  {stats.alertas_validade.length} alerta(s)
                </span>
              )}
            </h3>
            {stats?.alertas_validade?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.alertas_validade.map((alerta, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      alerta.urgencia === 'alta' 
                        ? 'bg-red-50 border-red-300' 
                        : 'bg-amber-50 border-amber-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">{alerta.ciclo_numero}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        alerta.urgencia === 'alta'
                          ? 'bg-red-600 text-white'
                          : 'bg-amber-500 text-white'
                      }`}>
                        {alerta.dias_restantes}d
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{alerta.cliente_nome}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Vence: {formatDate(alerta.validade)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                <p>Nenhum material próximo do vencimento</p>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-80">Ciclos Hoje</p>
              <p className="text-3xl font-bold">{stats?.ciclos_hoje || 0}</p>
            </div>
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-80">Taxa de Aprovação</p>
              <p className="text-3xl font-bold">{stats?.taxa_aprovacao || 0}%</p>
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-80">Tempo Médio</p>
              <p className="text-3xl font-bold">{stats?.tempo_medio_ciclo || 0}<span className="text-lg">min</span></p>
            </div>
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-80">Materiais Vencendo</p>
              <p className="text-3xl font-bold">{stats?.ciclos_vencendo || 0}</p>
            </div>
          </div>
        </TabsContent>

        {/* Gerencial Tab - KPIs Consolidados */}
        <TabsContent value="gerencial" className="space-y-6">
          {/* KPIs Principais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-5 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Total Ciclos (Mês)</p>
                  <p className="text-3xl font-bold">{stats?.ciclos || 0}</p>
                </div>
                <Activity className="w-10 h-10 opacity-30" />
              </div>
              <p className="text-xs mt-2 opacity-70">
                ✅ {stats?.ciclos_aprovados || 0} aprovados
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-5 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Taxa de Aprovação</p>
                  <p className="text-3xl font-bold">
                    {stats?.ciclos > 0 ? Math.round((stats?.ciclos_aprovados / stats?.ciclos) * 100) : 0}%
                  </p>
                </div>
                <ShieldCheck className="w-10 h-10 opacity-30" />
              </div>
              <p className="text-xs mt-2 opacity-70">
                Qualidade dos ciclos
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-5 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Clientes Ativos</p>
                  <p className="text-3xl font-bold">{stats?.clientes || 0}</p>
                </div>
                <Users className="w-10 h-10 opacity-30" />
              </div>
              <p className="text-xs mt-2 opacity-70">
                Base de clientes
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Materiais Cadastrados</p>
                  <p className="text-3xl font-bold">{stats?.materiais || 0}</p>
                </div>
                <Package className="w-10 h-10 opacity-30" />
              </div>
              <p className="text-xs mt-2 opacity-70">
                Catálogo ativo
              </p>
            </div>
          </div>
          
          {/* Gráficos Lado a Lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de Ciclos por Autoclave */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-blue-600" />
                Ciclos por Autoclave
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={advancedMetrics?.ciclos_por_autoclave || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="autoclave" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Total" />
                  <Bar dataKey="aprovados" fill="#10B981" radius={[4, 4, 0, 0]} name="Aprovados" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Gráfico de Distribuição por Status */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-purple-600" />
                Distribuição por Status
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Aprovados', value: stats?.ciclos_aprovados || 0, color: '#10B981' },
                      { name: 'Pendentes', value: stats?.ciclos_pendentes || 0, color: '#F59E0B' },
                      { name: 'Reprovados', value: (stats?.ciclos || 0) - (stats?.ciclos_aprovados || 0) - (stats?.ciclos_pendentes || 0), color: '#EF4444' },
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'Aprovados', value: stats?.ciclos_aprovados || 0, color: '#10B981' },
                      { name: 'Pendentes', value: stats?.ciclos_pendentes || 0, color: '#F59E0B' },
                      { name: 'Reprovados', value: (stats?.ciclos || 0) - (stats?.ciclos_aprovados || 0) - (stats?.ciclos_pendentes || 0), color: '#EF4444' },
                    ].filter(d => d.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Indicadores de Produtividade */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">📈 Indicadores de Produtividade</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">
                  {advancedMetrics?.media_ciclos_dia?.toFixed(1) || '0.0'}
                </p>
                <p className="text-sm text-slate-600 mt-1">Ciclos/Dia (Média)</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">
                  {advancedMetrics?.tempo_medio_ciclo || '15'}min
                </p>
                <p className="text-sm text-slate-600 mt-1">Tempo Médio/Ciclo</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">
                  {stats?.autoclaves || 0}
                </p>
                <p className="text-sm text-slate-600 mt-1">Autoclaves Ativas</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-orange-600">
                  {stats?.ciclos_vencendo || 0}
                </p>
                <p className="text-sm text-slate-600 mt-1">Materiais Vencendo</p>
              </div>
            </div>
          </div>
          
          {/* Top Clientes */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">🏆 Top 5 Clientes (Mais Ciclos)</h3>
            <div className="space-y-3">
              {(advancedMetrics?.top_clientes || []).slice(0, 5).map((cliente, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="font-medium text-slate-900">{cliente.nome}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-blue-600">{cliente.total_ciclos}</span>
                    <span className="text-sm text-slate-500 ml-1">ciclos</span>
                  </div>
                </div>
              ))}
              {(!advancedMetrics?.top_clientes || advancedMetrics.top_clientes.length === 0) && (
                <p className="text-center text-slate-500 py-4">Nenhum dado disponível</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart - Ciclos por Dia */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-blue-600" />
                Ciclos por Dia (Últimos 7 dias)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats?.cycles_by_day || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} name="Ciclos" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Autoclaves */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                Top Autoclaves
              </h3>
              <div className="space-y-3">
                {stats?.top_autoclaves?.map((autoclave, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                    <span className="text-sm font-medium text-slate-700">{autoclave.nome}</span>
                    <span className="text-lg font-bold text-blue-600">{autoclave.count}</span>
                  </div>
                ))}
                {(!stats?.top_autoclaves || stats.top_autoclaves.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhum dado disponível</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Cycles and Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Cycles */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Ciclos Recentes</h3>
              <div className="space-y-3">
                {stats?.recent_cycles?.map((ciclo) => (
                  <div
                    key={ciclo.id}
                    data-testid={`recent-cycle-${ciclo.id}`}
                    className="p-3 border border-slate-200 rounded-md hover:border-blue-300 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-500">{ciclo.numero_ciclo}</span>
                      <StatusBadge status={ciclo.resultado} />
                    </div>
                    <div className="text-xs text-slate-600">
                      {formatDateTime(ciclo.data_hora)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Stats */}
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Total de Ciclos</p>
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_ciclos || 0}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Pendentes</p>
                    <p className="text-2xl font-bold text-indigo-600">{stats?.ciclos_pendentes || 0}</p>
                  </div>
                  <Activity className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Reprovados</p>
                    <p className="text-2xl font-bold text-red-600">{stats?.ciclos_reprovados || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Taxa de Aprovação por Autoclave */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                Taxa de Aprovação por Autoclave
              </h3>
              {advancedMetrics?.autoclaves_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={advancedMetrics.autoclaves_stats} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} unit="%" stroke="#64748B" fontSize={12} />
                    <YAxis dataKey="nome" type="category" width={80} stroke="#64748B" fontSize={11} />
                    <Tooltip
                      formatter={(value) => [`${value}%`, 'Taxa']}
                      contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="taxa" fill="#10B981" radius={[0, 4, 4, 0]} name="Taxa %" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Nenhum dado disponível</p>
              )}
            </div>

            {/* Top Clientes */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Top 5 Clientes por Volume
              </h3>
              {advancedMetrics?.top_clientes?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={advancedMetrics.top_clientes} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#64748B" fontSize={12} />
                    <YAxis dataKey="nome" type="category" width={100} stroke="#64748B" fontSize={11} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="ciclos" fill="#8B5CF6" radius={[0, 4, 4, 0]} name="Ciclos" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Nenhum dado disponível</p>
              )}
            </div>
          </div>

          {/* Materiais por Tipo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-600" />
                Materiais por Tipo
              </h3>
              {advancedMetrics?.materiais_por_tipo?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={advancedMetrics.materiais_por_tipo}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ tipo, percent }) => `${tipo} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {advancedMetrics.materiais_por_tipo.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Nenhum dado disponível</p>
              )}
            </div>

            {/* Stats Summary */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Resumo de Performance</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium uppercase">Total Clientes</p>
                  <p className="text-2xl font-bold text-blue-900">{stats?.total_clientes || 0}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600 font-medium uppercase">Total Materiais</p>
                  <p className="text-2xl font-bold text-green-900">{stats?.total_materiais || 0}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium uppercase">Autoclaves Ativas</p>
                  <p className="text-2xl font-bold text-purple-900">{stats?.total_autoclaves || 0}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-600 font-medium uppercase">Agendamentos Pend.</p>
                  <p className="text-2xl font-bold text-orange-900">{advancedMetrics?.agendamentos_pendentes || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Evolução Mensal (Últimos 6 meses)
            </h3>
            {advancedMetrics?.cycles_by_month?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={advancedMetrics.cycles_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="aprovados" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Aprovados" />
                  <Area type="monotone" dataKey="reprovados" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} name="Reprovados" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">Nenhum dado disponível</p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Taxa de Aprovação Mensal */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Taxa de Aprovação Mensal</h3>
              {advancedMetrics?.cycles_by_month?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={advancedMetrics.cycles_by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                    <YAxis domain={[0, 100]} unit="%" stroke="#64748B" fontSize={12} />
                    <Tooltip
                      formatter={(value) => [`${value}%`, 'Taxa']}
                      contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Line type="monotone" dataKey="taxa" stroke="#2563EB" strokeWidth={2} dot={{ fill: '#2563EB' }} name="Taxa %" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Nenhum dado disponível</p>
              )}
            </div>

            {/* Volume Mensal */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Volume Total Mensal</h3>
              {advancedMetrics?.cycles_by_month?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={advancedMetrics.cycles_by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="total" fill="#2563EB" radius={[4, 4, 0, 0]} name="Total" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Nenhum dado disponível</p>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
